/**
 * FoldersTab — the "Products" page body.
 *
 * Header (eyebrow + "Your products" + subtext + black "New product"),
 * All/TikTok/Instagram filter tabs, a 3-col grid of minimal product cards,
 * a create modal, and a centred empty state.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Trash2, Loader2, ArrowRight } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const CYAN = '#06B6D4';

const cardDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};

const FoldersTab = () => {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [folders, setFolders] = useState([]);
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | tiktok | instagram
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [shopUrl, setShopUrl] = useState('');
  const [platform, setPlatform] = useState('tiktok');
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/folders`, { headers: getAuthHeader() });
      setFolders(res.data.folders || []);
      setLimits(res.data.limits || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load products');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setName(''); setNotes(''); setShopUrl(''); setPlatform('tiktok'); setShowForm(false);
  };

  const createFolder = async (e) => {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedUrl = shopUrl.trim();
    const hasUrl = platform === 'tiktok' && !!trimmedUrl;
    if (!trimmedName && !hasUrl) {
      toast.error('Give your product a name, or paste a TikTok Shop URL');
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(
        `${API}/api/folders`,
        { name: trimmedName || 'New product', notes: notes.trim(), platform },
        { headers: getAuthHeader() },
      );
      const folderId = res.data.id;

      // If a TikTok Shop URL was provided, auto-populate the product details
      // (and use the scraped title as the default name when none was typed).
      if (hasUrl) {
        try {
          const imp = await axios.post(
            `${API}/api/folders/${folderId}/import-product`,
            { url: trimmedUrl },
            { headers: getAuthHeader() },
          );
          const title = imp.data?.title;
          if (title && !trimmedName) {
            await axios.patch(`${API}/api/folders/${folderId}`, { name: title.slice(0, 80) }, { headers: getAuthHeader() });
          }
          toast.success('Product imported from TikTok Shop');
        } catch (impErr) {
          toast.error(impErr.response?.data?.detail || 'Product created, but we couldn’t import that URL');
        }
        resetForm();
        navigate(`/dashboard/folders/${folderId}`);
        return;
      }

      resetForm();
      toast.success('Product created');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create product');
    } finally {
      setCreating(false);
    }
  };

  const deleteFolder = async (id, folderName) => {
    if (!window.confirm(`Delete "${folderName}"? Its saved videos will be removed. Your generation history stays.`)) return;
    setDeleting(id);
    try {
      await axios.delete(`${API}/api/folders/${id}`, { headers: getAuthHeader() });
      toast.success('Product deleted');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete product');
    } finally {
      setDeleting(null);
    }
  };

  const counts = {
    all: folders.length,
    tiktok: folders.filter((f) => (f.platform || 'tiktok') === 'tiktok').length,
    instagram: folders.filter((f) => f.platform === 'instagram').length,
  };
  const visible = filter === 'all' ? folders : folders.filter((f) => (f.platform || 'tiktok') === filter);

  const TABS = [
    { slug: 'all', label: 'All' },
    { slug: 'tiktok', label: 'TikTok' },
    { slug: 'instagram', label: 'Instagram' },
  ];

  return (
    <div data-testid="folders-tab">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
        <div>
          <p className="text-[11px] font-mono uppercase tracking-[0.18em] mb-2" style={{ color: CYAN }}>
            Products
          </p>
          <h1 className="font-display text-[32px] leading-none font-semibold text-[#111111]">
            Your products
          </h1>
          <p className="text-sm text-[#6B6B6B] mt-2 max-w-xl">
            Each product keeps its winning videos, patterns and scripts together.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-[12px] bg-[#111111] text-white text-sm font-medium hover:-translate-y-0.5 transition-transform"
          data-testid="new-product-button"
        >
          <Plus className="w-4 h-4" strokeWidth={2} /> New product
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16" data-testid="folders-loading">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : folders.length === 0 ? (
        /* Empty state */
        <div className="text-center py-24" data-testid="products-empty">
          <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-[#9A9A9A] mb-3">No products yet</p>
          <p className="text-[#6B6B6B] mb-6">Start by pasting a winning video.</p>
          <Link to="/generator">
            <button className="inline-flex items-center gap-2 h-11 px-5 rounded-[12px] bg-[#111111] text-white text-sm font-medium hover:-translate-y-0.5 transition-transform" data-testid="products-empty-new-script">
              New script
            </button>
          </Link>
        </div>
      ) : (
        <>
          {/* Filter tabs */}
          <div className="flex items-center gap-6 border-b border-[#EFE8DD] mb-6" data-testid="products-filter">
            {TABS.map((t) => {
              const active = filter === t.slug;
              return (
                <button
                  key={t.slug}
                  type="button"
                  onClick={() => setFilter(t.slug)}
                  className={`pb-3 -mb-px text-sm transition-colors ${active ? 'text-[#111111] font-medium' : 'text-[#9A9A9A] hover:text-[#6B6B6B]'}`}
                  style={active ? { borderBottom: `2px solid ${CYAN}` } : undefined}
                  data-testid={`products-filter-${t.slug}`}
                >
                  {t.label} <span className="text-[#B7B2A8]">{counts[t.slug]}</span>
                </button>
              );
            })}
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="products-grid">
            {visible.map((f) => (
              <ProductCard key={f.id} folder={f} onDelete={deleteFolder} deleting={deleting === f.id} />
            ))}
          </div>

          {visible.length === 0 && (
            <p className="text-sm text-[#9A9A9A] py-10 text-center" data-testid="products-filter-empty">
              No {filter === 'tiktok' ? 'TikTok' : 'Instagram'} products yet.
            </p>
          )}
        </>
      )}

      {/* Create modal */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-[#FFFDF9] w-[calc(100vw-1.5rem)] max-w-lg p-4 sm:p-6 max-h-[90vh] overflow-y-auto rounded-xl" data-testid="new-product-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">New product</DialogTitle>
          </DialogHeader>
          <form onSubmit={createFolder} className="space-y-4">
            <div>
              <Label htmlFor="folder-name" className="text-xs mb-1 block">Product name</Label>
              <Input
                id="folder-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Whitening Strips"
                maxLength={80}
                autoFocus
                data-testid="new-folder-name"
              />
            </div>
            <div>
              <Label htmlFor="folder-notes" className="text-xs mb-1 block">Notes (optional)</Label>
              <Textarea
                id="folder-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Angles you've tried, commission %, buyer notes…"
                rows={3}
                maxLength={2000}
                data-testid="new-folder-notes"
              />
            </div>
            <div>
              <Label className="text-xs mb-1 block">Inspiration source</Label>
              <div className="inline-flex rounded-full border border-border bg-background/40 p-0.5 text-xs" data-testid="new-folder-platform-switch">
                {[{ slug: 'tiktok', label: 'TikTok' }, { slug: 'instagram', label: 'Instagram' }].map((p) => (
                  <button
                    key={p.slug}
                    type="button"
                    onClick={() => setPlatform(p.slug)}
                    className={'px-3 py-1.5 rounded-full transition font-medium ' + (platform === p.slug ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}
                    data-testid={`new-folder-platform-${p.slug}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">You can flip this later while the product has no videos.</p>
            </div>
            {platform === 'tiktok' && (
              <div>
                <Label htmlFor="folder-shop-url" className="text-xs mb-1 block">TikTok Shop URL (optional)</Label>
                <Input
                  id="folder-shop-url"
                  value={shopUrl}
                  onChange={(e) => setShopUrl(e.target.value)}
                  placeholder="Paste a TikTok Shop product link to auto-fill…"
                  data-testid="new-folder-shop-url"
                />
                <p className="text-[11px] text-muted-foreground mt-1">We’ll pull the product name, category, price, pain point &amp; benefits for you.</p>
              </div>
            )}
            <Button type="submit" variant="premium" disabled={creating} className="w-full" data-testid="new-folder-submit">
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              {creating ? (shopUrl.trim() && platform === 'tiktok' ? 'Importing…' : 'Creating…') : 'Create product'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ProductCard = ({ folder, onDelete, deleting }) => {
  const platform = folder.platform || 'tiktok';
  const platformLabel = platform === 'instagram' ? 'Instagram' : 'TikTok';
  const stats = `${folder.transcript_count} video${folder.transcript_count === 1 ? '' : 's'} · ${folder.generation_count} script${folder.generation_count === 1 ? '' : 's'}${folder.updated_at ? ` · Updated ${cardDate(folder.updated_at)}` : ''}`;
  return (
    <div className="group relative" data-testid={`folder-card-${folder.id}`}>
      <button
        type="button"
        onClick={() => onDelete(folder.id, folder.name)}
        disabled={deleting}
        className="absolute top-3 right-3 z-10 opacity-0 group-hover:opacity-100 transition-opacity text-[#B7B2A8] hover:text-destructive disabled:opacity-50"
        aria-label={`Delete ${folder.name}`}
        data-testid={`folder-delete-${folder.id}`}
      >
        {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
      </button>
      <Link
        to={`/dashboard/folders/${folder.id}`}
        className="block rounded-2xl bg-white border border-[#EAE3D8] p-5 transition-colors hover:border-[#D4D4D4]"
      >
        <div className="flex items-center justify-between gap-2 mb-6">
          <h3 className="text-base font-semibold text-[#111111] leading-tight line-clamp-2 pr-2">{folder.name}</h3>
          <span className="shrink-0 text-[11px] px-2.5 py-0.5 rounded-full bg-[#F3F1EC] text-[#6B6B6B]">{platformLabel}</span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <p className="text-[13px] text-[#9A9A9A]">{stats}</p>
          <span
            className="shrink-0 inline-flex items-center gap-1 h-8 px-3 rounded-full bg-[#111111] text-white text-xs font-medium group-hover:-translate-y-0.5 transition-transform"
            data-testid={`folder-open-${folder.id}`}
          >
            Open <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.2} />
          </span>
        </div>
      </Link>
    </div>
  );
};

export default FoldersTab;
